import React from 'react';
import LoadingSpinner from "../../../../components/AntCostomComponents/LoadingSpinner";
import {Button, Divider} from "antd";
import EyeWrinkle from "../../../../assets/wrinkles/eyewrinkle.png";
import LipWrinkle from "../../../../assets/wrinkles/lipwrinkle.png";
import ForeheadWrinkle from "../../../../assets/wrinkles/wrinkle.png";
import {useTranslation} from "react-i18next";
import styled from "styled-components";

const WrinklesAnalysisComponent = ({wrinkles, analysis, hideImages = false}) => {
    const {t} = useTranslation("common");
    const [loadingStates, setLoadingStates] = React.useState({});
    const [detectionDataMap, setDetectionDataMap] = React.useState({});
    const [calculatedFaceDataMap, setCalculatedFaceDataMap] = React.useState({});
    const [flipImageMap, setFlipImageMap] = React.useState({});

    // Analysis calculations from the actual data
    const calculateSeverity = (data) => {
        if (!data || !data.data) return {value: "Medium\nlevel", percentage: 60, color: "#F5CB5C"};

        const totalArea = data.data.crows_feet.area + data.data.forehead.area + data.data.lips.area;
        const maxArea = 2000;
        const percentage = Math.min(Math.round((totalArea / maxArea) * 100), 100);

        let value, color;
        if (percentage < 40) {
            value = "Low\nlevel";
            color = "#70B77E";
        } else if (percentage < 70) {
            value = "Medium\nlevel";
            color = "#F5CB5C";
        } else {
            value = "High\nlevel";
            color = "#E63946";
        }

        return {value, percentage, color};
    };

    const calculateDepth = (data) => {
        if (!data || !data.data) return {value: "Deep", percentage: 85, color: "#E63946"};

        const avgWidths = [
            data.data.crows_feet.avg_width,
            data.data.forehead.avg_width,
            data.data.lips.avg_width
        ];
        const maxWidth = Math.max(...avgWidths);
        const percentage = Math.min(Math.round((maxWidth / 2) * 100), 100);

        let value, color;
        if (percentage < 50) {
            value = "Shallow";
            color = "#70B77E";
        } else if (percentage < 80) {
            value = "Medium";
            color = "#F5CB5C";
        } else {
            value = "Deep";
            color = "#E63946";
        }

        return {value, percentage, color};
    };

    const calculateLength = (data) => {
        if (!data || !data.data) return {value: "Fine", percentage: 40, color: "#70B77E"};

        const totalLength = data.data.crows_feet.skeleton_length +
            data.data.forehead.skeleton_length +
            data.data.lips.skeleton_length;
        const maxLength = 50;
        const percentage = Math.min(Math.round((totalLength / maxLength) * 100), 100);

        let value, color;
        if (percentage < 50) {
            value = "Fine";
            color = "#70B77E";
        } else if (percentage < 80) {
            value = "Medium";
            color = "#F5CB5C";
        } else {
            value = "Extensive";
            color = "#E63946";
        }

        return {value, percentage, color};
    };

    const toggleFlip = (wrinkleId) => {
        setFlipImageMap(prev => ({
            ...prev,
            [wrinkleId]: !prev[wrinkleId]
        }));
    };

    React.useEffect(() => {
        const fetchData = async () => {
            // Initialize loading states for all wrinkles
            const initialLoadingStates = {};
            wrinkles.forEach(wrinkle => {
                initialLoadingStates[wrinkle.id] = true;
            });
            setLoadingStates(initialLoadingStates);

            // Initialize flip states
            const initialFlipStates = {};
            wrinkles.forEach(wrinkle => {
                initialFlipStates[wrinkle.id] = false;
            });
            setFlipImageMap(initialFlipStates);

            const detectionData = {};
            const calculatedData = {};

            // Fetch data for all wrinkles in parallel
            await Promise.all(wrinkles.map(async (wrinkle) => {
                try {
                    const response = await fetch(wrinkle.data_url);
                    const result = await response.json();

                    detectionData[wrinkle.id] = result;

                    calculatedData[wrinkle.id] = {
                        severity: calculateSeverity(result),
                        depth: calculateDepth(result),
                        length: calculateLength(result)
                    };

                    setLoadingStates(prev => ({
                        ...prev,
                        [wrinkle.id]: false
                    }));
                } catch (error) {
                    console.error(`Error fetching detection results for wrinkle ${wrinkle.id}:`, error);
                    setLoadingStates(prev => ({
                        ...prev,
                        [wrinkle.id]: false
                    }));
                }
            }));

            console.log('detectionData:', detectionData);
            setDetectionDataMap(detectionData);
            setCalculatedFaceDataMap(calculatedData);
        };

        if (wrinkles && wrinkles.length > 0) {
            fetchData();
        }

        console.log('Wrinkles analysis component mounted with data:', wrinkles);
    }, [wrinkles]);

    const WrinkleField = ({icon, title, area, skeleton, perimeter, average}) => {
        return (
            <div className="bg-[#f1f1f1] flex gap-3 justify-between rounded-xl p-3 w-full">
                <div className="shadow-xl bg-white rounded-xl flex flex-col items-center pb-2 relative"
                     style={{width: '5rem', height: '5rem'}}>
                    <img src={icon} alt="Wrinkle Icon" className="w-full"
                         style={{objectFit: 'cover', width: '3rem', height: '3rem'}}/>
                    <p>{title}</p>
                </div>
                <div className="flex flex-row gap-3 items-center w-full justify-evenly">
                    <div className="flex flex-col gap-2">
                        <p>{t("area")}: {area}mm<sup>2</sup></p>
                        <p>{t("perimeter")}: {perimeter}mm</p>
                    </div>

                    <Divider type="vertical" style={{height: "100%", borderColor: '#888888'}}/>

                    <div className="flex flex-col gap-2">
                        <p>{t("skeleton_length")}: {skeleton}mm</p>
                        <p>{t("avg_width")}: {average}mm</p>
                    </div>
                </div>
            </div>
        );
    };

    const renderWrinkleAnalysis = (wrinkle, index) => {
        const isLoading = loadingStates[wrinkle.id];
        const detectionData = detectionDataMap[wrinkle.id];
        console.log('detectionData for wrinkle:', wrinkle.id, detectionData);
        const calculatedFaceData = calculatedFaceDataMap[wrinkle.id];
        const isFlipped = flipImageMap[wrinkle.id];

        const formattedDate = wrinkle.created_at ? new Date(wrinkle.created_at).toLocaleDateString() : null;

        if (isLoading) {
            return (
                <div className="mb-8" key={wrinkle.id}>
                    <LoadingSpinner/>
                </div>
            );
        }

        return (
            <div className="mb-8" key={wrinkle.id}>
                <h3 className="font-bold text-lg mb-3">Analysis {index + 1} {formattedDate && `(${formattedDate})`}</h3>
                <div className={`grid grid-cols-1 ${hideImages ? '' : 'md:grid-cols-2'} gap-4`}>
                    {!hideImages && wrinkle.wrinkle_mask_url && (
                        <div className="text-center flex justify-between gap-3 rounded-xl overflow-hidden relative"
                             style={{maxHeight: '60vh', height: 'auto', width: 'fit-content'}}>
                            <img
                                src={analysis['image_url']}
                                alt="Image"
                                style={{
                                    objectFit: 'contain',
                                    width: '100%',
                                    height: '100%',
                                }}
                            />
                            {!isFlipped && (
                                <div
                                    className="absolute top-0 left-0 w-full h-full"
                                    style={{
                                        backgroundImage: `url(${wrinkle.wrinkle_mask_url})`,
                                        backgroundSize: '100% 100%',  // Changed from 'contain' to '100% 100%'
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        opacity: 0.7,
                                        mixBlendMode: 'screen',
                                        filter: 'hue-rotate(210deg) saturate(2)'
                                    }}
                                />
                            )}
                            <Button type="primary" className="absolute left-3 top-3"
                                    onClick={() => toggleFlip(wrinkle.id)}>Flip</Button>
                        </div>
                    )}

                    {calculatedFaceData && detectionData && (
                        <WrinkleWrapper
                            className="flex flex-col gap-3 justify-between items-start bg-white p-3 rounded-xl">
                            <h3 className="font-bold text-center text-black">Wrinkles level</h3>
                            {WrinkleField({
                                icon: EyeWrinkle,
                                title: t("eye"),
                                area: detectionData?.crows_feet?.area || 0,
                                skeleton: detectionData?.crows_feet?.skeleton_length || 0,
                                perimeter: detectionData?.crows_feet?.perimeter || 0,
                                average: detectionData?.crows_feet?.avg_width || 0,
                            })}
                            {WrinkleField({
                                icon: LipWrinkle,
                                title: t("lips"),
                                area: detectionData?.lips?.area || 0,
                                skeleton: detectionData?.lips?.skeleton_length || 0,
                                perimeter: detectionData?.lips?.perimeter || 0,
                                average: detectionData?.lips?.avg_width || 0,
                            })}
                            {WrinkleField({
                                icon: ForeheadWrinkle,
                                title: t("forehead"),
                                area: detectionData?.forehead?.area || 0,
                                skeleton: detectionData?.forehead?.skeleton_length || 0,
                                perimeter: detectionData?.forehead?.perimeter || 0,
                                average: detectionData?.forehead?.avg_width || 0,
                            })}
                        </WrinkleWrapper>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="w-full h-full">
            {wrinkles.length === 0 ? (
                <div className="text-center py-8">No wrinkle analysis data available</div>
            ) : (
                <div className="w-full">
                    {wrinkles.map((wrinkle, index) => renderWrinkleAnalysis(wrinkle, index))}
                </div>
            )}
        </div>
    );
};

const WrinkleWrapper = styled.div`
    * {
        color: black;
    }
`;

export default WrinklesAnalysisComponent;